import {assertDistinctReferenceReadings} from './referenceInstrumentEvidence.js';
import { uid } from './constants.js';
import { fromDisplay } from './units.js';
import { parseFieldNumber } from './fieldNumber.js';
import {referenceLength,solveReferenceDistances,referenceAnchorSnapshot,validateReferenceConstruction} from './twoDistanceReference.js';

export const FIELD_REFERENCE_LIMIT = 200;
const validID=value=>typeof value==='string'&&value.trim()===value&&value.length>0&&Array.from(value).length<=80;
const text=(value,limit,required=false)=>typeof value==='string'&&value.trim()===value&&Array.from(value).length<=limit&&(!required||value.length>0);
const coordinate=value=>typeof value==='number'&&Number.isFinite(value)&&Math.abs(value)<=100000;
const timestamp=value=>{
  if(typeof value!=='string'||value.length>40||!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(value)||!Number.isFinite(Date.parse(value)))return false;
  const year=Number(value.slice(0,4)),month=Number(value.slice(5,7)),day=Number(value.slice(8,10)),last=new Date(0);
  last.setUTCFullYear(year,month,0);
  return month>=1&&month<=12&&day>=1&&day<=last.getUTCDate()&&Number(value.slice(11,13))<=23&&Number(value.slice(14,16))<=59&&Number(value.slice(17,19))<=59;
};
const floorID=plan=>plan.activeFloorId||'ground';
function assertReferenceFloor(plan){
 if(!plan||plan._siteLevels||plan._groundSelections||plan._sceneWalls||plan.activeFloorId==='@site-ground')throw new Error('Choose one editable floor before locating a reference point.');
 const active=floorID(plan),floors=plan.floors??[];
 if(!validID(active)||!Array.isArray(floors)||floors.length>127)throw new Error('The Site needs valid distinct floor identities before using reference points.');
 const ids=new Set([active]);for(const floor of floors){if(!floor||!validID(floor.id)||ids.has(floor.id))throw new Error('The Site has ambiguous floor identities. Review its floors before using reference points.');ids.add(floor.id);}
}

/** Keep empty/invalid draft input distinct from a measured zero. Never clamp a
 * physical coordinate silently; callers retain the text so it can be fixed. */
export function parseReferenceCoordinate(value,doc) {
  const metres=fromDisplay(parseFieldNumber(value,{exponent:true}),doc);
  return coordinate(metres)?metres:NaN;
}

export function validateFieldReference(point) {
  if(!point||!validID(point.id)||!text(point.label,120,true)||!text(point.note,500)||!coordinate(point.x)||!coordinate(point.y)
      ||!['manual','two-distance'].includes(point.source)||!validID(point.recordedFloorID)||!timestamp(point.capturedAt)||!timestamp(point.updatedAt)
      ||Date.parse(point.updatedAt)<Date.parse(point.capturedAt))throw new Error('A saved reference point has invalid or incomplete data.');
  if(point.source==='two-distance')validateReferenceConstruction(point,{validID,text,timestamp});
  else if(point.construction!==undefined)throw new Error('A manual reference cannot claim derived construction evidence.');
  return point;
}

/** Fixed floor coordinates, never inferred from or moved with room geometry. */
export function fieldReferences(plan) {
  const points=plan.fieldReferences===undefined?[]:plan.fieldReferences;
  if(!Array.isArray(points)||points.length>FIELD_REFERENCE_LIMIT)throw new Error('This floor has an invalid reference-point list.');
  const ids=new Set();
  for(const point of points){
    validateFieldReference(point);
    if(ids.has(point.id))throw new Error('Saved reference points have duplicate identities.');
    ids.add(point.id);
  }
  return points;
}

function saveReference(plan,id,input) {
  const points=fieldReferences(plan),old=id?points.find(point=>point.id===id):null;
  if(id&&!old)throw new Error('The reference point no longer exists on this floor.');
  if(!id&&points.length>=FIELD_REFERENCE_LIMIT)throw new Error('This floor has reached its 200 reference point limit.');
  if(old&&old.recordedFloorID!==floorID(plan))throw new Error('This reference belongs to another floor. Record a new point on this floor.');
  if(old?.source==='two-distance'&&(input.x!==old.x||input.y!==old.y))throw new Error('A derived reference keeps its original coordinates. Record a new point to change its location.');
  const now=new Date().toISOString();
  const point={id:old?.id||uid('reference-'),label:typeof input.label==='string'?input.label.trim():input.label,
    x:input.x,y:input.y,note:typeof input.note==='string'?input.note.trim():input.note??'',source:old?.source||'manual',...(old?.construction?{construction:structuredClone(old.construction)}:{}),
    recordedFloorID:floorID(plan),capturedAt:old?.capturedAt||now,updatedAt:now};
  validateFieldReference(point);
  if(points.some(other=>other.id!==point.id&&other.label.toLocaleLowerCase('en-US')===point.label.toLocaleLowerCase('en-US')))throw new Error('Use a different reference name on this floor.');
  const doc=structuredClone(plan);
  doc.fieldReferences=id?points.map(existing=>structuredClone(existing.id===id?point:existing)):[...structuredClone(points),point];
  return {doc,point};
}

export const createFieldReference=(plan,input)=>saveReference(plan,null,input);
export const updateFieldReference=(plan,id,input)=>saveReference(plan,id,input);
export function removeFieldReference(plan,id) {
  const points=fieldReferences(plan);
  if(!points.some(point=>point.id===id))throw new Error('The reference point no longer exists on this floor.');
  const doc=structuredClone(plan);doc.fieldReferences=structuredClone(points.filter(point=>point.id!==id));return doc;
}

/** Snapshot accepted alignment inputs so later point edits cannot rewrite the
 * evidence attached to a placed scan. Coordinate edits never move old rooms. */
export function fieldReferenceTarget(plan,id) {
  const point=fieldReferences(plan).find(point=>point.id===id);
  if(!point||point.recordedFloorID!==floorID(plan))throw new Error('Choose a reference point recorded on this floor.');
  const assessment=assessFieldReference(plan,id);if(!assessment.usable)throw new Error(assessment.message);
  return {x:point.x,y:point.y,fieldReference:structuredClone(point)};
}


/** Read-only dependency assessment. A moved/removed anchor never moves saved C
 * or rewrites an earlier scan's accepted reference snapshot. */
function referenceAssessment(plan){
  assertReferenceFloor(plan);
  const points=fieldReferences(plan),byID=new Map(points.map(p=>[p.id,p])),memo=new Map();
  function assess(key,path){
   if(memo.has(key))return memo.get(key);
   const p=byID.get(key);if(!p)return{status:'missing-anchor',usable:false,message:'A reference anchor no longer exists on this floor.'};
   if(p.recordedFloorID!==floorID(plan))return{status:'different-floor',usable:false,message:'This reference was recorded on another floor.'};
   if(path.has(key))return{status:'invalid',usable:false,message:'Reference dependencies contain a cycle.'};
   if(p.source==='manual')return{status:'current',usable:true,message:'Operator-entered coordinates; check the physical mark independently.'};
   const next=new Set(path);next.add(key);let metadataChanged=false;
   for(const anchor of[p.construction.anchors.a,p.construction.anchors.b]){
    const current=byID.get(anchor.id);if(!current)return{status:'missing-anchor',usable:false,message:'A recorded anchor was removed. This point stays at its saved coordinates; record a new point after checking the anchors.'};
    if(['id','x','y','recordedFloorID','source'].some(k=>current[k]!==anchor[k]))return{status:'anchor-changed',usable:false,message:'A recorded anchor changed. This point stays at its saved coordinates; review the construction and record a new point.'};
    metadataChanged ||= current.label!==anchor.label||current.updatedAt!==anchor.updatedAt;
    const result=assess(anchor.id,next);if(!result.usable)return{...result,message:'An upstream reference needs review. '+result.message};metadataChanged ||= !!result.metadataChanged;
   }
   const result={status:'current',usable:true,metadataChanged,message:metadataChanged?'Anchor details changed; their coordinates still match the recorded construction. This is not a new measurement.':'Constructed from two recorded distances and recorded anchors; this is not independent survey verification.'};memo.set(key,result);return result;
  }
  return {points,assess:key=>assess(key,new Set())};
}
export function assessFieldReference(plan,id){
 try{return referenceAssessment(plan).assess(id);}
 catch(error){return{status:'invalid',usable:false,message:error.message};}
}
/** Validate once and share dependency results across a bounded floor review.
 * Malformed collections throw so callers cannot silently omit their records. */
export function assessFieldReferences(plan){
 const {points,assess}=referenceAssessment(plan);
 return new Map(points.map(point=>[point.id,assess(point.id)]));
}
export function reviewTwoDistanceReference(plan,{aID,bID,aLength,bLength,side}={}){
 assertReferenceFloor(plan);
 const points=fieldReferences(plan);
 if(aID===bID||!validID(aID)||!validID(bID))throw new Error('Choose two distinct existing anchor references on this floor.');
 if(!['left','right'].includes(side))throw new Error('Choose explicitly the left or right side of A → B.');
 const selected=[aID,bID].map(id=>{const status=assessFieldReference(plan,id);if(!status.usable)throw new Error(status.message);return points.find(p=>p.id===id);});
 const first=referenceLength(aLength),second=referenceLength(bLength);
 assertDistinctReferenceReadings(first,second);
 const solved=solveReferenceDistances(...selected,first.metres,second.metres),choice=solved.solutions.find(s=>s.side===side);
 if(!choice.valid)throw new Error(choice.reason);
 return{point:{x:choice.x,y:choice.y},solutions:solved.solutions,anchors:{a:referenceAnchorSnapshot(selected[0]),b:referenceAnchorSnapshot(selected[1]),baselineMetres:solved.baselineMetres},aLength:first,bLength:second,warnings:[
 'A and B are existing reference coordinates. Their baseline must be checked independently; two recorded distances do not verify those coordinates.',
 'Left and right are mirrored solutions relative to A → B in the Site plan. Choose the physical side explicitly.',
 'The lengths must describe horizontal distances between the same marks. Sloping tape/laser distances and instrument offsets are not corrected automatically.',
 'No room, wall, previous scan or existing reference moves. Later anchor changes require review of this saved construction.'
 ]};
}
export function createTwoDistanceReference(plan,input){
 const review=reviewTwoDistanceReference(plan,input),created=createFieldReference(plan,{label:input.label,note:input.note,...review.point});
 const point={...created.point,source:'two-distance',construction:{version:1,side:input.side,anchors:structuredClone(review.anchors),aLength:structuredClone(review.aLength),bLength:structuredClone(review.bLength)}};
 validateFieldReference(point);created.doc.fieldReferences[created.doc.fieldReferences.length-1]=point;
 return{doc:created.doc,point,review};
}
